import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Chip, Empty, PageHead, Panel, Skeleton } from '../components/ui.tsx';
import { get, qs } from '../lib/api.ts';
import { relative } from '../lib/format.ts';

interface Event {
  id: number;
  at: string;
  actorLabel: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  diff: Record<string, { from: unknown; to: unknown }> | null;
  reason: string | null;
  ip: string | null;
}

const tone = (a: string) => (a.includes('failed') || a.includes('mismatch') || a.includes('rejected') || a.includes('cancel') ? 'bad' : a.includes('sensitive') || a.includes('revealed') || a.includes('export') ? 'warn' : a.includes('approved') || a.includes('login') ? 'ok' : 'plain');

export function Audit() {
  const [entityType, setEntityType] = useState('');
  const q = useInfiniteQuery({
    queryKey: ['audit', entityType],
    queryFn: ({ pageParam }) => get<Event[]>(`/api/audit${qs({ entityType, before: pageParam })}`),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.length === 100 ? last.at(-1)!.id : undefined),
  });
  const rows = q.data?.pages.flat() ?? [];
  return (
    <div>
      <PageHead
        eyebrow="Audit trail"
        title={
          <>
            Who did what, <em>and why.</em>
          </>
        }
        lede="Append-only. Sensitive values are recorded as changed, never stored in clear."
      />
      <div className="toolbar">
        <select className="select" style={{ width: 'auto' }} value={entityType} onChange={(e) => setEntityType(e.target.value)} aria-label="Entity type">
          <option value="">Everything</option>
          {['employee', 'user', 'payroll_run', 'leave_request', 'timesheet_correction', 'attendance_event', 'setting', 'oauth_client', 'device', 'component_rule'].map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
      <Panel>
        {q.isLoading ? (
          <Skeleton h={300} />
        ) : !rows.length ? (
          <Empty title="Nothing recorded yet." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Subject</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="mono faint" title={new Date(e.at).toLocaleString()}>
                    {relative(e.at)}
                  </td>
                  <td>{e.actorLabel ?? 'system'}</td>
                  <td>
                    <Chip tone={tone(e.action)}>{e.action}</Chip>
                  </td>
                  <td className="mono faint" style={{ fontSize: 11 }}>
                    {e.entityType}
                    {e.entityId ? ` · ${e.entityId.slice(0, 8)}` : ''}
                  </td>
                  <td className="muted" style={{ fontSize: 'var(--step--2)', maxWidth: 360 }}>
                    {e.reason ? <div>“{e.reason}”</div> : null}
                    {e.diff
                      ? Object.entries(e.diff)
                          .slice(0, 4)
                          .map(([k, v]) => (
                            <div key={k} className="mono">
                              {k}: {JSON.stringify(v?.from)} → {JSON.stringify(v?.to)}
                            </div>
                          ))
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {q.hasNextPage ? (
          <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
            <Button onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>
              Load older
            </Button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
